import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  getPrefix,
  addPrefix,
  checkCodes,
  getAllServices,
  addSale,
  getSingleSale,
  updateSale,
  getUnits,
  searchParties,
  searchProduct,
  getAllExpenses,
} from "../../services/services";

import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { toastError, toastSuccess } from "../../utils";
import Loading from "../../components/loading";
import AddPartyModal from "../Modal/AddPartyModal";
import ServiceAutoSuggest from "./components/ServiceAutoSuggest";
import PrefixModal from "../Modal/PrefixModal";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import ZoomControls from "../zoomControls";
import AsyncSearchSelect from "./components/AsyncSearchSelect";

import { confirmClearAll } from "../../components/confirmDelete";
const CreateSalePage = () => {
  const [savedExpenses, setSavedExpenses] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const user = JSON.parse(localStorage.getItem("Login_User")) || {};
  const location = useLocation();

  const referenceType = location?.state || null;

  const { _id } = user;
  useEffect(() => {
    // If NO id → means new invoice → clear draft
    if (!id) {
      localStorage.removeItem(`pendingSale_${_id}`);
    }
  }, [id, _id]);
  //   console.log("_id:", _id);
  // console.log("Key to remove:", `pendingSale_${_id}`);
  // console.log("Exists in localStorage?", localStorage.getItem(`pendingSale_${_id}`));

  const [partyId, setPartyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [isAddingParty, setIsAddingParty] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedPartyFull, setSelectedPartyFull] = useState();
  const [codeError, setCodeError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPrefixValue, setNewPrefixValue] = useState("");
  const [prefixList, setPrefixList] = useState(["noPrefix"]);
  const [attachments, setAttachments] = useState([]);
  const [showBuyersDropdown, setShowBuyersDropdown] = useState(false);
  const [allUnits, setAllUnits] = useState([]);

  const [save, setSave] = useState(false);

  const loadProductOptions = async (input) => {
    if (!input || input.trim().length < 2) {
      return [];
    }

    try {
      const res = await searchProduct({ search: input });

      const products = res?.data?.response || [];

      return products.map((p) => ({
        label: p.name,
        value: p._id,
        raw: p,
      }));
    } catch (err) {
      console.error("Product search failed:", err);
      return [];
    }
  };

  const loadPartyOptions = async (input, type = "sale") => {
    if (!input || input.trim().length < 2) {
      return [];
    }

    try {
      const partyTypeFilter = type === "sale" ? "customer,both" : "vendor,both";

      const res = await searchParties({
        search: input,
        partyType: partyTypeFilter,
      });
      if (res?.data?.status) {
        return res.data.response.map((p) => ({
          label: `${p.name} (${p.partyType || "Party"})`,
          value: p._id,
          data: p,
        }));
      }

      return [];
    } catch (err) {
      console.error("Party search failed:", err);
      return [];
    }
  };

  useEffect(() => {
    const resizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        const overlay = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (overlay) {
          overlay.style.display = "none";
        }
      }
    };

    window.addEventListener("error", resizeObserverError);
    return () => window.removeEventListener("error", resizeObserverError);
  }, []);

  useEffect(() => {
    const loadUnits = async () => {
      try {
        const res = await getUnits({ search: "" });
        if (res.data?.status) {
          setAllUnits(res.data.response.units || []);
        }
      } catch (err) {
        console.error("Failed to load units", err);
        toast.error("Could not load product units");
      }
    };
    loadUnits();
  }, []);

  useEffect(() => {
    fetchPrefixes();
  }, []);

  const fetchPrefixes = async () => {
    try {
      const { data } = await getPrefix("sale");
      const prefixes = data.prefixes.map((p) => p.name);
      setPrefixList(["noPrefix", ...prefixes]);
    } catch (err) {
      toast.error("Failed to load prefixes");
      setPrefixList(["noPrefix"]);
    }
  };

  // ───── DEFAULT EMPTY ROWS ─────
  const emptyItem = {
    itemId: "",
    itemName: "",
    unit: "",
    sellingPrice: 0,
    sellingQty: 1,
    taxType: "exclusive",
    gstRate: 0,
    quantity: 0,
  };
  const emptyService = {
    serviceId: "",
    name: "",
    details: "",
    baseCost: 0,
    qty: 0,
    taxType: "exclusive",
    gstRate: 0,
  };
  const emptyExpense = {
    expenseId: "",
    name: "",
    price: 0,
    taxType: "exclusive",
    gstRate: 0,
  };

  const defaultFormData = {
    paymentMode: "cash",
    totalAmount: 0,
    receivedAmount: 0,
    dueAmount: 0,
    items: [{ ...emptyItem }],
    services: [{ ...emptyService }],
    otherExpenses: [{ ...emptyExpense }],
    customerName: "",
    prefix: "noPrefix",
    invoiceType: "billOfSupply",
    invoiceNumber: "",
    manualCode: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    transportationMode: "",
    lrNo: "",
    vehicleNo: "",
    transporterId: "",
    transporterName: "",
    shippingAddress: "",
    lrDate: "",
    discountValue: "",
    discountType: "amount", // "amount" | "percent"
  };
  const [formData, setFormData] = useState(() => {
    if (!id && _id) {
      const savedData = localStorage.getItem(`pendingSale_${_id}`);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          return {
            ...defaultFormData,
            ...parsed,
            items: parsed.items?.length ? parsed.items : [{ ...emptyItem }],
            services: parsed.services?.length
              ? parsed.services
              : [{ ...emptyService }],
            otherExpenses: parsed.otherExpenses?.length
              ? parsed.otherExpenses
              : [{ ...emptyExpense }],
          };
        } catch (e) {
          console.warn("Invalid saved data, using defaults:", e);
        }
      }
    }
    return defaultFormData;
  });
  const handleCodeCheck = useCallback(
    debounce(async (code) => {
      if (!code.trim()) {
        setCodeError("");
        return;
      }
      try {
        const { data } = await checkCodes({ code });
        setCodeError(data.valid ? "" : data.message);
      } catch {
        setCodeError("Error checking code");
      }
    }, 600),
    [],
  );
  useEffect(() => {
    if (!id && _id) {
      localStorage.setItem(`pendingSale_${_id}`, JSON.stringify(formData));
    }
  }, [formData, id, _id]);

  const handlePartySelect = (selected) => {
    if (!selected?._id) return;
    setPartyId(selected._id);

    setFormData((prev) => ({
      ...prev,
      customerName: selected.name,
      customerId: selected._id,
    }));

    // Use already fetched party list
    setSelectedPartyFull(selected);
  };
  const addItemRow = () => {
    setFormData((p) => ({
      ...p,
      items: [...(p.items || []), { ...emptyItem }],
    }));
  };
  const addServiceRow = () => {
    setFormData((p) => ({
      ...p,
      services: [...(p.services || []), { ...emptyService }],
    }));
  };
  const addExpenseRow = () => {
    setFormData((p) => ({
      ...p,
      otherExpenses: [...(p.otherExpenses || []), { ...emptyExpense }],
    }));
  };
  const removeItemRow = (index) => {
    setFormData((prev) => {
      const newItems = prev.items.filter((_, i) => i !== index);
      return {
        ...prev,
        items: newItems.length === 0 ? [{ ...emptyItem }] : newItems,
      };
    });
  };
  const removeServiceRow = (index) => {
    setFormData((prev) => {
      const newServices = prev.services.filter((_, i) => i !== index);
      return {
        ...prev,
        services:
          newServices.length === 0 ? [{ ...emptyService }] : newServices,
      };
    });
  };
  const removeExpenseRow = (index) => {
    setFormData((prev) => {
      const newExpenses = prev.otherExpenses.filter((_, i) => i !== index);
      return {
        ...prev,
        otherExpenses:
          newExpenses.length === 0 ? [{ ...emptyExpense }] : newExpenses,
      };
    });
  };
  const handleItemChange = (index, field, value) => {
    let updatedValue = value;

    // ---- SELLING PRICE (allow decimal) ----
    if (field === "sellingPrice") {
      if (!/^\d*\.?\d*$/.test(value)) return;
    }

    // ---- SELLING QTY (numbers only) ----
    if (field === "sellingQty") {
      updatedValue = value.replace(/[^0-9]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: updatedValue } : item,
      ),
    }));
  };
  const handleServiceUpdate = (index, updatedService) => {
    setFormData((prev) => {
      const updated = [...prev.services];
      updated[index] = {
        ...updated[index],
        ...updatedService,
      };
      return { ...prev, services: updated };
    });
  };
  const handleExpenseChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.otherExpenses];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, otherExpenses: updated };
    });
  };

  const handlePrefixChange = (e) => {
    const value = e.target.value;
    if (value === "addNew") {
      setShowAddModal(true);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      prefix: value,
      invoiceNumber:
        value === "noPrefix"
          ? prev.manualCode
          : `${value}-${prev.manualCode || ""}`,
    }));
  };
  // ───── Add New Prefix ─────
  const handleAddPrefix = async () => {
    const prefix = newPrefixValue.trim().toUpperCase();
    if (!prefix) return toast.error("Prefix cannot be empty");
    try {
      await addPrefix({ name: prefix, type: "sale" });
      await fetchPrefixes();
      setFormData((prev) => ({
        ...prev,
        prefix,
        invoiceNumber: `${prefix}-`,
      }));
      setNewPrefixValue("");
      setShowAddModal(false);
      toast.success("Prefix added successfully");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add prefix");
    }
  };

  useEffect(() => {
    if (formData.invoiceNumber) {
      handleCodeCheck(formData.invoiceNumber);
    }
  }, [formData.invoiceNumber, handleCodeCheck]);

  const servicesTotal = useMemo(
    () =>
      (formData.services || []).reduce((sum, service) => {
        const cost = Number(service.baseCost) || 0;
        const qty = Number(service.qty) || 1;
        const gstRate = Number(service.gstRate) || 0;
        const isInclusive = service.taxType === "inclusive";

        const base = cost * qty;
        return isInclusive ? sum + base : sum + base + (base * gstRate) / 100;
      }, 0),
    [formData.services],
  );

  const expensesTotal = useMemo(
    () =>
      (formData.otherExpenses || []).reduce((sum, exp) => {
        const amount = parseFloat(exp.price) || 0;
        const gstRate = parseFloat(exp.gstRate) || 0;
        const isInclusive = exp.taxType === "inclusive";
        if (isInclusive && gstRate > 0) return sum + amount;
        return sum + amount + (amount * gstRate) / 100;
      }, 0),
    [formData.otherExpenses],
  );
  const itemsTotalWithGST = useMemo(
    () =>
      (formData.items || []).reduce((sum, item) => {
        const qty = parseFloat(item.sellingQty) || 0;
        const price = parseFloat(item.sellingPrice) || 0;
        const gstRate = parseFloat(item.gstRate) || 0;
        const isInclusive = item.taxType === "inclusive";

        let total = qty * price;

        if (!isInclusive && gstRate > 0) {
          total += (total * gstRate) / 100;
        }

        return sum + total;
      }, 0),
    [formData.items],
  );

  const totalAmount = useMemo(
    () => itemsTotalWithGST + servicesTotal + expensesTotal,
    [itemsTotalWithGST, servicesTotal, expensesTotal],
  );
  // Better safety + rounding
  const discountAmount = useMemo(() => {
    const raw = parseFloat(formData.discountValue) || 0;
    if (raw <= 0) return 0;

    let amount = 0;
    if (formData.discountType === "percent") {
      amount = (totalAmount * raw) / 100;
    } else {
      amount = Math.min(raw, totalAmount);
    }

    return Number(amount.toFixed(2)); // consistent 2 decimals
  }, [formData.discountValue, formData.discountType, totalAmount]);

  const finalTotal = useMemo(() => {
    return Math.max(0, totalAmount - discountAmount);
  }, [totalAmount, discountAmount]);

  const dueAmount = useMemo(() => {
    return finalTotal - (parseFloat(formData.receivedAmount) || 0);
  }, [finalTotal, formData.receivedAmount]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, totalAmount, dueAmount }));
  }, [totalAmount, dueAmount]);

  const fetchServices = async () => {
    try {
      const res = await getAllServices();
      const data = res.data;

      if (data.status) setServices(data.response.services);
    } catch (err) {
      console.error("Failed to load services", err);
    }
  };

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      try {
        if (id) {
          await getSale();
        }

        // await
        // fetchServices();

        await Promise.all([fetchServices(), fetchExpenses()]);
      } catch (err) {
        console.error("Failed to load data", err);
        toastError("Failed to load required data");
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [id]);

  const fetchExpenses = async () => {
    try {
      const response = await getAllExpenses({});

      const fetchedExpenses = response?.data?.response?.expenses ?? [];

      setSavedExpenses(fetchedExpenses);
    } catch (error) {
      console.error("Failed to load expenses:", error);
      setSavedExpenses([]);
    }
  };

  const getSale = async () => {
    try {
      const response = await getSingleSale(id);
      console.log("Single Sale:", response.data.response.singleSale);
      console.log("Due Amount:", response.data.response.singleSale.dueAmount);
      const { singleSale } = response.data.response;
      const customerId = singleSale.customerId?._id || singleSale.customerId;
      setPartyId(customerId);
      const customer = singleSale.customerId;

      if (customer) {
        setPartyId(customer._id);
        setSelectedPartyFull(customer);
      }
      // Parse invoice number
      let prefix = "noPrefix";
      let manualCode = "";
      let invoiceNumber = singleSale.invoiceNumber || "";
      if (invoiceNumber && invoiceNumber.includes("-")) {
        const parts = invoiceNumber.split("-");
        prefix = parts[0];
        manualCode = parts.slice(1).join("-");
      } else if (invoiceNumber) {
        manualCode = invoiceNumber;
      }
      // Set form data first
      setFormData((prev) => ({
        ...prev,
        customerId,
        customerName: "",
        prefix,
        invoiceType: singleSale.invoiceType || "taxInvoice",
        invoiceNumber,
        manualCode,
        invoiceDate:
          singleSale.saleDate?.split("T")[0] ||
          new Date().toISOString().split("T")[0],
        items:
          singleSale.items?.length > 0
            ? singleSale.items.map((item) => {
                const product = item.productId || {};
                const availableQty = product.batches
                  ? product.batches.reduce(
                      (sum, batch) => sum + (batch.remainingQty || 0),
                      0,
                    )
                  : 0;
                return {
                  itemId: product._id || item.productId || "",
                  itemName: product.name || item.itemName || "",
                  sellingQty: item.sellingQty || 0,
                  unit: product.unit || item.unit || "",
                  sellingPrice: item.sellingPrice || 0,
                  taxType: item.taxType || "exclusive",
                  gstRate: item.gstRate || 0,
                  quantity: availableQty,
                };
              })
            : [{ ...emptyItem }],
        services:
          singleSale.services?.length > 0
            ? singleSale.services.map((s) => ({
                serviceId: s.serviceId || "",
                name: s.name || "",
                qty: s.qty || 1,
                details: s.details || "",
                baseCost: Number(s.baseCost) || 0,
                taxType: s.taxType || "exclusive",
                gstRate: Number(s.gstRate) || 0,
              }))
            : [{ ...emptyService }],

        otherExpenses: singleSale.otherExpenses || [{ ...emptyExpense }],

        discountValue: singleSale.discountValue || "",
        discountType: singleSale.discountType || "amount",
        totalAmount: singleSale.totalAmount || 0,
        receivedAmount: singleSale.receivedAmount || 0,
        dueAmount: singleSale.dueAmount || 0,
        paymentMode: singleSale.paymentMode || "cash",
        transportationMode: singleSale.transportation?.mode || "",
        lrNo: singleSale.transportation?.lrNo || "",
        vehicleNo: singleSale.transportation?.vehicleNo || "",
        transporterId: singleSale.transportation?.transporterId || "",
        transporterName: singleSale.transportation?.transporterName || "",
        shippingAddress: singleSale.transportation?.shippingAddress || "",
        lrDate: singleSale.transportation?.lrDate?.split("T")[0] || "",
      }));
      setAttachments(
        (singleSale.attachments || []).map((file) => ({
          ...file,
          preview: `${process.env.REACT_APP_IMAGE_URL}${file.filePath}`,
        })),
      );
    } catch (error) {
      console.error("Error loading sale:", error);
      toastError("Failed to load sale");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSave(true);

    // Vehicle Number Validation
    if (
      formData.vehicleNo &&
      !/^[A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{4}$/i.test(
        formData.vehicleNo.replace(/\s/g, ""),
      )
    ) {
      toastError("Enter valid vehicle number");
      return;
    }

    // LR Number Validation
    if (formData.lrNo && !/^[a-zA-Z0-9/-]{3,20}$/.test(formData.lrNo)) {
      toastError("Enter valid LR number");
      return;
    }

    // Transporter Name Validation
    if (
      formData.transporterName &&
      !/^[a-zA-Z\s.&-]{2,50}$/.test(formData.transporterName)
    ) {
      toastError("Enter valid transporter name");
      return;
    }

    // Transporter ID Validation
    if (
      formData.transporterId &&
      !/^[A-Z0-9]{4,20}$/.test(formData.transporterId)
    ) {
      toastError("Enter valid transporter ID");
      return;
    }

    if (
      formData.prefix &&
      formData.prefix !== "noPrefix" &&
      !formData.manualCode?.trim()
    ) {
      setCodeError("Please enter invoice number");
      setSave(false);
      toast.error("Please enter invoice number");
      return;
    }

    const validItems = formData.items.filter((i) => i.itemName);
    const validServices = formData.services.filter((s) => s.name);
    if (!id) {
      if (!validItems.length && !validServices.length) {
        setSave(false);
        return toastError("Add at least one item or service");
      }
    }
    const payload = {
      customerId: partyId,
      items: validItems.map((i) => ({
        productId: i.itemId,
        itemName: i.itemName,
        sellingQty: i.sellingQty,
        unit: i.unit,
        sellingPrice: i.sellingPrice,
        taxType: i.taxType,
        gstRate: i.gstRate,
      })),
      // FIX: Filter empty services before mapping; add gstRate to payload
      services: formData.services
        .filter((s) => s.name?.trim())
        .map((s) => {
          const baseCost = Number(s.baseCost) || 0;
          const qty = Number(s.qty) || 1;
          const gstRate = Number(s.gstRate) || 0;
          const isInclusive = s.taxType === "inclusive";

          const baseAmount = qty * baseCost;
          let gstAmount = 0;
          let total = baseAmount;

          if (isInclusive && gstRate > 0) {
            gstAmount = (baseAmount * gstRate) / (100 + gstRate);
            total = baseAmount;
          } else if (!isInclusive && gstRate > 0) {
            gstAmount = (baseAmount * gstRate) / 100;
            total = baseAmount + gstAmount;
          }

          return {
            serviceId: s.serviceId || "",
            name: s.name,
            qty: qty,
            details: s.details || "",
            baseCost: baseCost,
            baseAmount: Number(baseAmount.toFixed(2)),
            gstAmount: Number(gstAmount.toFixed(2)),
            total: Number(total.toFixed(2)),
            taxType: s.taxType || "exclusive",
            gstRate: gstRate,
          };
        }),

      otherExpenses: formData.otherExpenses.filter((e) => e.name?.trim()),
      totalAmount: Number(finalTotal.toFixed(2)),
      discountValue: parseFloat(formData.discountValue) || 0,
      discountType: formData.discountType,
      discountAmount: Number(discountAmount.toFixed(2)),
      receivedAmount: parseFloat(formData.receivedAmount) || 0,
      dueAmount: Number(dueAmount.toFixed(2)),
      paymentMode: formData.paymentMode,
      invoiceType: formData.invoiceType || "taxInvoice",
      invoiceNumber: formData.invoiceNumber?.trim() || "",
      transportationMode: formData.transportationMode,
      lrNo: formData.lrNo,
      vehicleNo: formData.vehicleNo,
      transporterId: formData.transporterId,
      transporterName: formData.transporterName,
      shippingAddress: formData.shippingAddress,
      lrDate: formData.lrDate,
      existingAttachments: attachments
        .filter((item) => !item.file) // old files
        .map((item) => ({
          fileName: item.fileName,
          filePath: item.filePath,
        })),
    };
    // console.log("Sending payload:", JSON.stringify(payload, null, 2));
    try {
      // const response = await (id ? updateSale(id, payload) : addSale(payload));
      const formDataToSend = new FormData();

      // append json payload
      formDataToSend.append("data", JSON.stringify(payload));

      // append attachments
      attachments.forEach((item) => {
        if (item.file instanceof File) {
          formDataToSend.append("attachments", item.file);
        }
      });

      const response = await (id
        ? updateSale(id, formDataToSend)
        : addSale(formDataToSend));
      toastSuccess(response.data.message);
      if (!id && _id) localStorage.removeItem(`pendingSale_${_id}`);
      navigate("/sales");
    } catch (error) {
      toastError(error.response?.data?.message || "Operation failed");
    } finally {
      setSave(false);
    }
  };
  const handleCancel = () => {
    if (!id && _id) localStorage.removeItem(`pendingSale_${_id}`);
    navigate(-1);
  };

  return (
    <>
      {/* <ZoomControls /> */}

      <div id="main" className="add-item-page">
        <div className="d-flex justify-content-between align-items-center mb-3 px-2">
          <h4 className="mb-0 custom-title">{id ? "Update" : "Create"} Sale</h4>
          <div className="text-end">
            {/* <button
              type="button"
              className="btn btn-primary me-2 text-nowrap"
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to clear all fields? This cannot be undone.",
                  )
                ) {
                  setFormData({
                    ...defaultFormData,
                    invoiceDate: new Date().toISOString().split("T")[0],
                  });
                  setPartyId("");
                  setSelectedPartyFull(null);
                  setCodeError("");
                  if (_id) {
                    localStorage.removeItem(`pendingSale_${_id}`);
                  }
                  toastSuccess("All fields cleared!");
                }
              }}
            >
              Clear All
            </button> */}

            <button
              type="button"
              className="btn btn-primary me-2 text-nowrap"
              onClick={async () => {
                const confirmation = await confirmClearAll();

                if (confirmation) {
                  setFormData({
                    ...defaultFormData,
                    invoiceDate: new Date().toISOString().split("T")[0],
                  });

                  setPartyId("");
                  setSelectedPartyFull(null);
                  setCodeError("");

                  if (_id) {
                    localStorage.removeItem(`pendingSale_${_id}`);
                  }

                  toastSuccess("All fields cleared!");
                }
              }}
            >
              Clear All
            </button>
          </div>
        </div>
        {loading ? (
          <Loading />
        ) : (
          <div className="content-wrapper">
            <form onSubmit={handleSubmit}>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="card mb-4">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <h5 className="fw-bold text-muted text-nowrap mb-0">
                          Party Detail
                        </h5>{" "}
                        <div className="d-flex ">
                          <div className="position-relative">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm text-nowrap"
                              onClick={() =>
                                setShowBuyersDropdown((prev) => !prev)
                              }
                            >
                              <i className="bi bi-user"></i>
                              Search Buyer
                            </button>
                            {showBuyersDropdown && (
                              <>
                                <div
                                  className="position-fixed top-0 start-0 w-100 h-100"
                                  style={{ zIndex: 1040 }}
                                  onClick={() => setShowBuyersDropdown(false)}
                                />
                                <div
                                  className="seller-dropdown position-absolute top-100 mt-2 shadow-lg border rounded-3 overflow-hidden"
                                  style={{
                                    background: "white",
                                    minWidth: "250px",
                                  }}
                                >
                                  <AsyncSearchSelect
                                    value={
                                      selectedPartyFull
                                        ? {
                                            label: `${selectedPartyFull.name} (${selectedPartyFull.partyType || "Party"})`,
                                            value: selectedPartyFull._id,
                                            data: selectedPartyFull,
                                          }
                                        : null
                                    }
                                    onChange={(option) => {
                                      if (option) {
                                        setPartyId(option.value);
                                        setSelectedPartyFull(option.data);
                                        setFormData((prev) => ({
                                          ...prev,
                                          customerName: option.data.name,
                                          customerId: option.value,
                                        }));
                                      } else {
                                        // cleared
                                        setPartyId("");
                                        setSelectedPartyFull(null);
                                        setFormData((prev) => ({
                                          ...prev,
                                          customerName: "",
                                          customerId: "",
                                        }));
                                      }
                                    }}
                                    loadOptions={(input) =>
                                      loadPartyOptions(input, "sale")
                                    }
                                    placeholder="Search buyer / party..."
                                    minInputLength={2}
                                    debounceWait={400}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    // menuPosition="fixed"
                                  />
                                </div>
                              </>
                            )}
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm text-nowrap mb-1"
                            onClick={() => setIsAddingParty("sale")}
                          >
                            Add Buyer
                          </button>
                        </div>
                      </div>
                      {selectedPartyFull && (
                        <>
                          <hr style={{ borderTop: "2px dashed #999" }} />

                          <div className="col-md-12 mt-2">
                            <div style={{ fontSize: "13px" }}>
                              <h5 className="mb-2 fw-bold">
                                {selectedPartyFull.name || "-"}
                              </h5>

                              <div className="row mb-1">
                                <div className="col-4 text-muted">Mobile</div>
                                <div className="col-8">
                                  {selectedPartyFull.mobile || "-"}
                                </div>
                              </div>

                              <div className="row mb-1">
                                <div className="col-4 text-muted">Email</div>
                                <div className="col-8">
                                  {selectedPartyFull.email || "-"}
                                </div>
                              </div>

                              <div className="row mb-1">
                                <div className="col-4 text-muted">GSTIN</div>
                                <div className="col-8">
                                  {selectedPartyFull.gstin || "-"}
                                </div>
                              </div>

                              <div className="row">
                                <div className="col-4 text-muted">Address</div>
                                <div className="col-8">
                                  {selectedPartyFull?.billingAddress?.[0]
                                    ?.address || "-"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className=" card p-3">
                    <div className="row g-3">
                      {/* Tax Invoice / Bill of Supply Radio Buttons */}
                      <div className="col-12">
                        <div className="d-flex align-items-center gap-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="invoiceType"
                              id="billOfSupply"
                              value="billOfSupply"
                              checked={formData.invoiceType === "billOfSupply"}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  invoiceType: e.target.value,
                                }))
                              }
                            />
                            <label
                              className="form-check-label fw-medium"
                              htmlFor="billOfSupply"
                            >
                              Bill of Supply
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="invoiceType"
                              id="taxInvoice"
                              value="taxInvoice"
                              checked={formData.invoiceType === "taxInvoice"}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  invoiceType: e.target.value,
                                }))
                              }
                            />
                            <label
                              className="form-check-label fw-medium"
                              htmlFor="taxInvoice"
                            >
                              Tax Invoice
                            </label>
                          </div>
                        </div>
                      </div>
                      {/* Invoice Prefix + Invoice No. */}
                      <div className="d-flex align-items-center gap-3 mb-1">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center gap-2">
                            {/* Prefix Dropdown */}
                            <select
                              className="form-select w-auto"
                              value={formData.prefix || "noPrefix"}
                              onChange={handlePrefixChange}
                            >
                              <option value="noPrefix">No Prefix</option>
                              {prefixList
                                .filter((p) => p !== "noPrefix")
                                .map((p) => (
                                  <option key={p} value={p}>
                                    {p}
                                  </option>
                                ))}
                              <option
                                value="addNew"
                                className="text-primary fw-bold"
                              >
                                + Add New Prefix
                              </option>
                            </select>
                            <div className="input-group flex-fill">
                              <input
                                type="text"
                                className={`form-control ${codeError ? "is-invalid mt-4" : ""}`}
                                value={formData.manualCode || ""}
                                onChange={(e) => {
                                  let value = e.target.value
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9]/g, "")
                                    .slice(0, 12);

                                  const prefix =
                                    formData.prefix &&
                                    formData.prefix !== "noPrefix"
                                      ? formData.prefix
                                      : "";

                                  const fullCode = prefix
                                    ? `${prefix}-${value}`
                                    : value;

                                  // remove error when typing
                                  if (value.trim()) {
                                    setCodeError("");
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    manualCode: value,
                                    invoiceNumber: fullCode,
                                  }));
                                }}
                              />
                              {codeError && (
                                <div className="invalid-feedback">
                                  {codeError}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Invoice Date */}
                      <div className="col-6 mb-2">
                        <label className="form-label text-muted small mb-1">
                          {formData.invoiceType === "billOfSupply"
                            ? "Bill Date"
                            : "Invoice Date"}
                        </label>
                        <div className="input-group">
                          <input
                            type="date"
                            className="form-control"
                            value={formData.invoiceDate || ""}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                invoiceDate: e.target.value,
                              }));
                            }}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card mb-4">
                <div className="card-body">
                  {/* ───── ITEMS TABLE ───── */}
                  {(formData.items || []).length > 0 && (
                    <div className="mb-4">
                      <label className="form-label fw-bold mb-3">
                        Items & Products
                      </label>

                      <div className="table-responsive border rounded">
                        <table
                          className="table table-bordered align-middle mb-0"
                          style={{
                            minWidth: "100%",
                            width: "max-content",
                            tableLayout: "fixed",
                          }}
                        >
                          <thead
                            className="table-light"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <tr>
                              <th style={{ width: "20%", minWidth: "180px" }}>
                                Item/Product
                              </th>
                              <th style={{ width: "8%", minWidth: "80px" }}>
                                In Stock
                              </th>
                              <th style={{ width: "10%", minWidth: "90px" }}>
                                Unit
                              </th>
                              <th style={{ width: "12%", minWidth: "110px" }}>
                                Sale Price / Unit
                              </th>

                              <th style={{ width: "8%", minWidth: "90px" }}>
                                Qty
                              </th>
                              <th style={{ width: "12%", minWidth: "110px" }}>
                                Total Price
                              </th>
                              <th style={{ width: "10%", minWidth: "110px" }}>
                                Tax Type
                              </th>
                              <th style={{ width: "10%", minWidth: "90px" }}>
                                GST %
                              </th>
                              <th
                                className="text-nowrap"
                                style={{ width: "12%", minWidth: "100px" }}
                              >
                                Total GST (₹)
                              </th>
                              <th style={{ width: "14%", minWidth: "110px" }}>
                                Total (₹)
                              </th>
                              <th style={{ width: "6%", minWidth: "70px" }}>
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.items.map((item, index) => {
                              const qty = parseFloat(item.sellingQty) || 0;
                              const price = parseFloat(item.sellingPrice) || 0;
                              const baseAmount = qty * price;
                              const gstRate = parseFloat(item.gstRate) || 0;
                              const isInclusive = item.taxType === "inclusive";
                              let gstAmount = 0;
                              if (isInclusive && gstRate > 0) {
                                gstAmount =
                                  (baseAmount * gstRate) / (100 + gstRate);
                              } else if (!isInclusive && gstRate > 0) {
                                gstAmount = (baseAmount * gstRate) / 100;
                              }
                              const total = isInclusive
                                ? baseAmount
                                : baseAmount + gstAmount;

                              return (
                                <tr key={index}>
                                  <td
                                    style={{ width: "20%", minWidth: "180px" }}
                                  >
                                    <AsyncSearchSelect
                                      value={
                                        item.itemId
                                          ? {
                                              label: item.itemName,
                                              value: item.itemId,
                                            }
                                          : null
                                      }
                                      loadOptions={loadProductOptions}
                                      onChange={(selected) => {
                                        if (!selected) {
                                          handleItemChange(index, "itemId", "");
                                          handleItemChange(
                                            index,
                                            "itemName",
                                            "",
                                          );
                                          handleItemChange(
                                            index,
                                            "sellingPrice",
                                            0,
                                          );
                                          handleItemChange(
                                            index,
                                            "quantity",
                                            0,
                                          );
                                          handleItemChange(
                                            index,
                                            "purchasePrice",
                                            0,
                                          );
                                          handleItemChange(index, "unit", "");
                                          handleItemChange(
                                            index,
                                            "taxType",
                                            "",
                                          );
                                          handleItemChange(index, "gstRate", 0);
                                          return;
                                        }

                                        const product =
                                          selected.raw || selected;

                                        handleItemChange(
                                          index,
                                          "itemId",
                                          product._id || selected.value,
                                        );
                                        handleItemChange(
                                          index,
                                          "itemName",
                                          product.name || selected.label,
                                        );

                                        handleItemChange(
                                          index,
                                          "sellingPrice",
                                          product.salePrice || 0,
                                        );
                                        handleItemChange(
                                          index,
                                          "quantity",
                                          product.stock || 0,
                                        );
                                        handleItemChange(
                                          index,
                                          "purchasePrice",
                                          product.latestPurchasePrice || 0,
                                        );
                                        handleItemChange(
                                          index,
                                          "unit",
                                          product.unit || "",
                                        );
                                        handleItemChange(
                                          index,
                                          "taxType",
                                          product.taxType || "exclusive",
                                        );
                                        handleItemChange(
                                          index,
                                          "gstRate",
                                          product.gstRate || 0,
                                        );
                                      }}
                                      placeholder="Search product..."
                                      minInputLength={2}
                                      debounceWait={400}
                                      isClearable
                                    />
                                  </td>

                                  <td style={{ width: "8%", minWidth: "80px" }}>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={item.quantity || 0}
                                      readOnly
                                      style={{ width: "100%" }}
                                    />
                                  </td>
                                  <td
                                    style={{ width: "10%", minWidth: "90px" }}
                                  >
                                    <select
                                      className="form-select form-select-sm"
                                      value={item.unit || ""}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "unit",
                                          e.target.value,
                                        )
                                      }
                                      style={{ width: "100%" }}
                                    >
                                      <option value="">Select Unit</option>
                                      {allUnits
                                        .filter((u) => u.status === "active") // only show active units
                                        .map((unit) => (
                                          <option
                                            key={unit._id}
                                            value={unit.name}
                                          >
                                            {unit.name}
                                          </option>
                                        ))}
                                    </select>
                                  </td>

                                  <td
                                    style={{ width: "12%", minWidth: "110px" }}
                                  >
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="form-control form-control-sm"
                                      value={item.sellingPrice}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "sellingPrice",
                                          e.target.value,
                                        )
                                      }
                                      style={{ width: "100%" }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "10%", minWidth: "90px" }}
                                  >
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      className="form-control form-control-sm "
                                      value={item.sellingQty}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "sellingQty",
                                          e.target.value,
                                        )
                                      }
                                      style={{ width: "100%" }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "12%", minWidth: "110px" }}
                                  >
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={baseAmount}
                                      readOnly
                                      style={{ width: "100%" }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "12%", minWidth: "110px" }}
                                  >
                                    <select
                                      className="form-select form-select-sm"
                                      value={item.taxType}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "taxType",
                                          e.target.value,
                                        )
                                      }
                                      style={{ width: "100%" }}
                                    >
                                      <option value="exclusive">
                                        Exclusive
                                      </option>
                                      <option value="inclusive">
                                        Inclusive
                                      </option>
                                    </select>
                                  </td>

                                  <td
                                    style={{ width: "10%", minWidth: "90px" }}
                                  >
                                    <select
                                      className="form-select form-select-sm"
                                      value={item.gstRate}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "gstRate",
                                          e.target.value,
                                        )
                                      }
                                      style={{ width: "100%" }}
                                    >
                                      {[0, 5, 12, 18, 28].map((r) => (
                                        <option key={r} value={r}>
                                          {r}%
                                        </option>
                                      ))}
                                    </select>
                                  </td>

                                  <td
                                    style={{ width: "10%", minWidth: "100px" }}
                                    className="text-end"
                                  >
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={gstAmount.toFixed(2)}
                                      readOnly
                                    />
                                  </td>

                                  <td
                                    style={{ width: "12%", minWidth: "110px" }}
                                  >
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={total.toFixed(2)}
                                      readOnly
                                      style={{ width: "100%" }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "6%", minWidth: "70px" }}
                                    className="text-center"
                                  >
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeItemRow(index)}
                                      style={{ minWidth: "40px" }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="text-start">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm mt-4"
                          onClick={addItemRow}
                          // disabled={!canAddItemRow()}
                        >
                          + Add Item
                        </button>
                      </div>
                    </div>
                  )}
                  <hr style={{ borderTop: "2px dashed #999" }} />

                  {/* ───── SERVICES TABLE ───── */}
                  {(formData.services || []).length > 0 && (
                    <div className="mt-4">
                      <label
                        htmlFor="services"
                        className="form-label fw-bold mb-3"
                      >
                        Services
                      </label>

                      <div className="table-responsive border rounded">
                        <table
                          className="table table-bordered align-middle mb-0"
                          style={{
                            minWidth: "100%",
                            width: "max-content",
                            tableLayout: "fixed",
                          }}
                        >
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "22%", minWidth: "180px" }}>
                                Service
                              </th>
                              <th style={{ width: "18%", minWidth: "150px" }}>
                                Details
                              </th>
                              <th style={{ width: "10%", minWidth: "80px" }}>
                                Qty
                              </th>
                              <th style={{ width: "12%", minWidth: "100px" }}>
                                Price / Unit
                              </th>
                              <th style={{ width: "12%", minWidth: "100px" }}>
                                Total Price (₹)
                              </th>
                              <th style={{ width: "12%", minWidth: "100px" }}>
                                Tax Type
                              </th>
                              <th style={{ width: "10%", minWidth: "90px" }}>
                                GST %
                              </th>
                              <th
                                style={{ width: "12%", minWidth: "100px" }}
                                className="text-nowrap"
                              >
                                Total GST (₹)
                              </th>
                              <th style={{ width: "12%", minWidth: "100px" }}>
                                Total (₹)
                              </th>
                              <th style={{ width: "7%", minWidth: "70px" }}>
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.services.map((s, i) => {
                              const qty = parseFloat(s.qty) || 1;
                              const unitPrice = parseFloat(s.baseCost) || 0;
                              const cost = unitPrice;
                              const gstRate = parseFloat(s.gstRate) || 0;
                              const isInclusive = s.taxType === "inclusive";
                              let gstAmount = 0;
                              const baseAmount = cost * qty;

                              if (isInclusive && gstRate > 0) {
                                gstAmount =
                                  (baseAmount * gstRate) / (100 + gstRate);
                              } else if (!isInclusive && gstRate > 0) {
                                gstAmount = (baseAmount * gstRate) / 100;
                              }
                              const total = isInclusive
                                ? baseAmount
                                : baseAmount + gstAmount;

                              return (
                                <tr key={i}>
                                  <td
                                    style={{ width: "25%", minWidth: "200px" }}
                                  >
                                    <ServiceAutoSuggest
                                      key={s.serviceId || `service-${i}`}
                                      services={services}
                                      selectedServiceId={s.serviceId}
                                      onSelect={(selected) => {
                                        if (!selected?._id) return;

                                        handleServiceUpdate(i, {
                                          serviceId: selected._id,
                                          name: selected.name,
                                          baseCost:
                                            Number(selected.baseCost) || 0,
                                          details: selected.details || "",
                                          gstRate:
                                            Number(selected.gstRate) || 0,
                                          taxType:
                                            selected.taxType || "exclusive",
                                          qty: 1,
                                        });
                                      }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "18%", minWidth: "150px" }}
                                  >
                                    <textarea
                                      className="form-control form-control-sm"
                                      rows="1"
                                      value={s.details || ""}
                                      onChange={(e) =>
                                        handleServiceUpdate(i, {
                                          details: e.target.value,
                                        })
                                      }
                                      style={{
                                        width: "100%",
                                        resize: "vertical",
                                      }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "10%", minWidth: "80px" }}
                                  >
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      className="form-control form-control-sm"
                                      value={s.qty}
                                      onChange={(e) => {
                                        const numericValue =
                                          e.target.value.replace(/[^0-9]/g, "");

                                        handleServiceUpdate(i, {
                                          qty: numericValue,
                                        });
                                      }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "12%", minWidth: "100px" }}
                                  >
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="form-control form-control-sm"
                                      value={s.baseCost ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        if (!/^\d*\.?\d*$/.test(value)) return;

                                        handleServiceUpdate(i, {
                                          baseCost: value,
                                        });
                                      }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "12%", minWidth: "100px" }}
                                  >
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={baseAmount}
                                      readOnly
                                    />
                                  </td>

                                  <td
                                    style={{ width: "12%", minWidth: "100px" }}
                                  >
                                    <select
                                      className="form-select form-select-sm"
                                      value={s.taxType || "exclusive"}
                                      onChange={(e) =>
                                        handleServiceUpdate(i, {
                                          taxType: e.target.value,
                                        })
                                      }
                                      style={{ width: "100%" }}
                                    >
                                      <option value="exclusive">
                                        Exclusive
                                      </option>
                                      <option value="inclusive">
                                        Inclusive
                                      </option>
                                    </select>
                                  </td>

                                  <td
                                    style={{ width: "10%", minWidth: "90px" }}
                                  >
                                    <select
                                      className="form-select form-select-sm"
                                      value={s.gstRate || 0}
                                      onChange={(e) =>
                                        handleServiceUpdate(i, {
                                          gstRate: Number(e.target.value),
                                        })
                                      }
                                      style={{ width: "100%" }}
                                    >
                                      {[0, 5, 12, 18, 28].map((r) => (
                                        <option key={r} value={r}>
                                          {r}%
                                        </option>
                                      ))}
                                    </select>
                                  </td>

                                  <td
                                    style={{ width: "12%", minWidth: "100px" }}
                                  >
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={gstAmount.toFixed(2)}
                                      readOnly
                                      style={{ width: "100%" }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "12%", minWidth: "100px" }}
                                  >
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={total.toFixed(2)}
                                      readOnly
                                      style={{ width: "100%" }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "7%", minWidth: "70px" }}
                                    className="text-center"
                                  >
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeServiceRow(i)}
                                      style={{ minWidth: "40px" }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="text-start mt-3">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm mt-2"
                          onClick={addServiceRow}
                          // disabled={!canAddServiceRow()}
                        >
                          + Add Service
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ───── Expenses TABLE ───── */}
                  <hr style={{ borderTop: "2px dashed #999" }} />
                  {(formData.otherExpenses || []).length > 0 && (
                    <div className="mt-4">
                      <label
                        htmlFor="expenses"
                        className="form-label fw-bold mb-3"
                      >
                        Expenses
                      </label>

                      <div className="table-responsive border rounded">
                        <table
                          className="table table-bordered align-middle mb-0"
                          style={{
                            minWidth: "100%",
                            width: "max-content",
                            tableLayout: "fixed",
                          }}
                        >
                          <thead
                            className="table-light"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <tr>
                              <th style={{ width: "25%", minWidth: "200px" }}>
                                Expense Name
                              </th>
                              <th style={{ width: "15%", minWidth: "120px" }}>
                                Price (₹)
                              </th>
                              <th style={{ width: "15%", minWidth: "120px" }}>
                                Tax Type
                              </th>
                              <th style={{ width: "15%", minWidth: "120px" }}>
                                GST %
                              </th>
                              <th style={{ width: "15%", minWidth: "120px" }}>
                                GST Amount (₹)
                              </th>
                              <th style={{ width: "15%", minWidth: "120px" }}>
                                Total (₹)
                              </th>
                              <th style={{ width: "10%", minWidth: "80px" }}>
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.otherExpenses.map((e, i) => {
                              const amount = parseFloat(e.price) || 0;
                              const gst = parseFloat(e.gstRate) || 0;
                              const inc = e.taxType === "inclusive";
                              let gstAmt = 0;
                              let total = amount;
                              if (inc && gst > 0) {
                                gstAmt = amount - amount / (1 + gst / 100);
                              } else if (!inc && gst > 0) {
                                gstAmt = (amount * gst) / 100;
                                total += gstAmt;
                              }

                              return (
                                <tr key={i}>
                                  <td
                                    style={{ width: "25%", minWidth: "200px" }}
                                  >
                                    {/* <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={e.name}
                                      onChange={(ev) =>
                                        handleExpenseChange(
                                          i,
                                          "name",
                                          ev.target.value,
                                        )
                                      }
                                      style={{ width: "100%" }}
                                    /> */}

                                    <select
                                      className="form-select form-select-sm"
                                      value={e.expenseId || ""}
                                      onChange={(ev) => {
                                        const selectedExpense =
                                          savedExpenses.find(
                                            (item) =>
                                              item._id === ev.target.value,
                                          );

                                        if (!selectedExpense) {
                                          handleExpenseChange(
                                            i,
                                            "expenseId",
                                            "",
                                          );
                                          handleExpenseChange(i, "name", "");
                                          handleExpenseChange(i, "price", 0);
                                          return;
                                        }

                                        handleExpenseChange(
                                          i,
                                          "expenseId",
                                          selectedExpense._id,
                                        );

                                        handleExpenseChange(
                                          i,
                                          "name",
                                          selectedExpense.title,
                                        );

                                        handleExpenseChange(
                                          i,
                                          "price",
                                          Number(selectedExpense.amount) || 0,
                                        );
                                      }}
                                      style={{ width: "100%" }}
                                    >
                                      <option value="">Select Expense</option>

                                      {savedExpenses.length === 0 ? (
                                        <option value="" disabled>
                                          No Expenses Available
                                        </option>
                                      ) : (
                                        savedExpenses.map((item) => (
                                          <option
                                            key={item._id}
                                            value={item._id}
                                          >
                                            {item.title}
                                          </option>
                                        ))
                                      )}
                                    </select>
                                  </td>

                                  <td
                                    style={{ width: "15%", minWidth: "120px" }}
                                  >
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="form-control form-control-sm"
                                      value={e.price}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (!/^\d*\.?\d*$/.test(value)) return;
                                        handleExpenseChange(i, "price", value);
                                      }}
                                      style={{ width: "100%" }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "15%", minWidth: "120px" }}
                                  >
                                    <select
                                      className="form-select form-select-sm"
                                      value={e.taxType}
                                      onChange={(ev) =>
                                        handleExpenseChange(
                                          i,
                                          "taxType",
                                          ev.target.value,
                                        )
                                      }
                                      style={{ width: "100%" }}
                                    >
                                      <option value="exclusive">
                                        Exclusive
                                      </option>
                                      <option value="inclusive">
                                        Inclusive
                                      </option>
                                    </select>
                                  </td>

                                  <td
                                    style={{ width: "15%", minWidth: "120px" }}
                                  >
                                    <select
                                      className="form-select form-select-sm"
                                      value={e.gstRate}
                                      onChange={(ev) =>
                                        handleExpenseChange(
                                          i,
                                          "gstRate",
                                          ev.target.value,
                                        )
                                      }
                                      style={{ width: "100%" }}
                                    >
                                      {[0, 5, 12, 18, 28].map((r) => (
                                        <option key={r} value={r}>
                                          {r}%
                                        </option>
                                      ))}
                                    </select>
                                  </td>

                                  <td
                                    style={{ width: "15%", minWidth: "120px" }}
                                  >
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={(gstAmt || 0).toFixed(2)}
                                      readOnly
                                      style={{ width: "100%" }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "15%", minWidth: "120px" }}
                                  >
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={(total || 0).toFixed(2)}
                                      readOnly
                                      style={{ width: "100%" }}
                                    />
                                  </td>

                                  <td
                                    style={{ width: "10%", minWidth: "80px" }}
                                    className="text-center"
                                  >
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => removeExpenseRow(i)}
                                      style={{ minWidth: "40px" }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="text-start mt-3">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm mt-2"
                          onClick={addExpenseRow}
                        >
                          + Add Expense
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Transportation & payment */}
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    {/* Transportation Section */}
                    <div className="col-md-6">
                      <div className="mb-4">
                        <h5 className="text-dark fw-bold mb-4">
                          Transportation Details
                        </h5>
                        <div className="p-4 border rounded bg-light">
                          <div className="row">
                            {/* Mode */}
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-medium">
                                Transport Mode
                              </label>
                              <select
                                className="form-select"
                                value={formData.transportationMode}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    transportationMode: e.target.value,
                                  }))
                                }
                              >
                                <option value="none">None</option>
                                <option value="road">Road</option>
                                <option value="rail">Rail</option>
                                <option value="air">Air</option>
                                <option value="ship">Ship</option>
                              </select>
                            </div>

                            {/* LR Date */}
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-medium">
                                LR Date
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                value={formData.lrDate}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    lrDate: e.target.value,
                                  }))
                                }
                              />
                            </div>

                            {/* LR No */}
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-medium">
                                LR Number
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter LR number"
                                value={formData.lrNo}
                                // onChange={(e) =>
                                //   setFormData((p) => ({
                                //     ...p,
                                //     lrNo: e.target.value,
                                //   }))
                                // }
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    lrNo: e.target.value.replace(
                                      /[^a-zA-Z0-9/-]/g,
                                      "",
                                    ),
                                  }))
                                }
                              />
                            </div>

                            {/* Vehicle No */}
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-medium">
                                Vehicle Number
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter vehicle number"
                                value={formData.vehicleNo}
                                // onChange={(e) =>
                                //   setFormData((p) => ({
                                //     ...p,
                                //     vehicleNo: e.target.value,
                                //   }))
                                // }
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    vehicleNo: e.target.value
                                      .toUpperCase()
                                      .replace(/[^A-Z0-9]/g, ""),
                                  }))
                                }
                              />
                            </div>

                            {/* Transporter Name */}
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-medium">
                                Transporter Name
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter transporter name"
                                value={formData.transporterName}
                                // onChange={(e) =>
                                //   setFormData((p) => ({
                                //     ...p,
                                //     transporterName: e.target.value,
                                //   }))
                                // }
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    transporterName: e.target.value.replace(
                                      /[^a-zA-Z\s.&-]/g,
                                      "",
                                    ),
                                  }))
                                }
                              />
                            </div>

                            {/* Transporter ID */}
                            <div className="col-md-6 mb-3">
                              <label className="form-label fw-medium">
                                Transporter ID
                              </label>
                              <input
                                type="text"
                                className="form-control text-uppercase"
                                placeholder="FD1243SR"
                                value={formData.transporterId}
                                // onChange={(e) =>
                                //   setFormData((p) => ({
                                //     ...p,
                                //     transporterId: e.target.value.toUpperCase(),
                                //   }))
                                // }
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    transporterId: e.target.value
                                      .toUpperCase()
                                      .replace(/[^A-Z0-9]/g, ""),
                                  }))
                                }
                              />
                            </div>

                            {/* Shipping Address */}
                            <div className="col-12 mb-0">
                              <label className="form-label fw-medium">
                                Shipping Address
                              </label>
                              <textarea
                                className="form-control"
                                rows="3"
                                placeholder="Enter complete shipping address"
                                value={
                                  formData.shippingAddress ||
                                  selectedPartyFull?.shippingAddress
                                    ?.map((a) => a.address)
                                    .join(", ") ||
                                  selectedPartyFull?.billingAddress
                                    ?.map((a) => a.address)
                                    .join(", ") ||
                                  ""
                                }
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    shippingAddress: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card mb-3">
                        <div className="card-header fw-semibold">
                          Attachments
                        </div>

                        <div className="card-body">
                          <input
                            type="file"
                            multiple
                            className="form-control"
                            onChange={(e) => {
                              const files = Array.from(e.target.files).map(
                                (file) => ({
                                  file,
                                  name: file.name,
                                  preview: URL.createObjectURL(file),
                                }),
                              );

                              setAttachments((prev) => [...prev, ...files]);
                            }}
                          />

                          {attachments.length > 0 && (
                            <div className="row mt-3">
                              {attachments.map((file, index) => (
                                <div key={index} className="col-md-3 mb-3">
                                  <div
                                    className="card p-2 text-center"
                                    style={{ position: "relative" }}
                                  >
                                    {/* Remove Button Top Right */}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setAttachments((prev) =>
                                          prev.filter((_, i) => i !== index),
                                        )
                                      }
                                      style={{
                                        position: "absolute",
                                        top: "5px",
                                        right: "5px",
                                        border: "none",
                                        background: "#dc3545",
                                        color: "#fff",
                                        borderRadius: "50%",
                                        width: "22px",
                                        height: "22px",
                                        fontSize: "12px",
                                        cursor: "pointer",
                                        lineHeight: "18px",
                                      }}
                                    >
                                      ✕
                                    </button>

                                    <img
                                      src={file.preview}
                                      alt={file.name || file.fileName}
                                      style={{
                                        width: "100%",
                                        height: "120px",
                                        objectFit: "cover",
                                        borderRadius: "6px",
                                      }}
                                    />
                                    {/* <small className="mt-2">
                                          {file.name || file.fileName}
                                        </small> */}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Payment Summary Section */}
                    <div className="col-md-6">
                      <div className="mb-4">
                        <h5 className="text-dark fw-bold mb-4">
                          Payment Summary
                        </h5>

                        {/* Totals Box */}
                        <div className="p-4 border rounded bg-light mb-1">
                          {/* Section Subtotals */}
                          <div className="mb-2">
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">Items Total:</span>
                              <span className="fw-medium">
                                ₹ {itemsTotalWithGST.toFixed(2)}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">
                                Services Total:
                              </span>
                              <span className="fw-medium">
                                ₹ {servicesTotal.toFixed(2)}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between mb-3">
                              <span className="text-muted">
                                Expenses Total:
                              </span>
                              <span className="fw-medium">
                                ₹ {expensesTotal.toFixed(2)}
                              </span>
                            </div>
                          </div>

                          <hr style={{ borderTop: "2px dashed #999" }} />

                          {/* Taxable Amount */}
                          <div className="mb-1">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="">
                                Taxable Amount (Without GST):
                              </span>
                              <span className=" text-dark">
                                ₹{" "}
                                {(() => {
                                  let taxable = 0;

                                  // Items calculation
                                  (formData.items || []).forEach((item) => {
                                    const qty =
                                      parseFloat(item.sellingQty) || 0;
                                    const price =
                                      parseFloat(item.sellingPrice) || 0;
                                    const rate = parseFloat(item.gstRate) || 0;
                                    let base = qty * price;
                                    if (
                                      item.taxType === "inclusive" &&
                                      rate > 0
                                    ) {
                                      base = (base * 100) / (100 + rate);
                                    }
                                    taxable += base;
                                  });

                                  // Services calculation
                                  (formData.services || []).forEach(
                                    (service) => {
                                      const unitCost =
                                        parseFloat(service.baseCost) || 0;
                                      const qty = parseFloat(service.qty) || 1;
                                      const rate =
                                        parseFloat(service.gstRate) || 0;

                                      let base = unitCost * qty;

                                      if (
                                        service.taxType === "inclusive" &&
                                        rate > 0
                                      ) {
                                        base = (base * 100) / (100 + rate);
                                      }

                                      taxable += base;
                                    },
                                  );

                                  // Expenses calculation
                                  (formData.otherExpenses || []).forEach(
                                    (exp) => {
                                      let amount = parseFloat(exp.price) || 0;
                                      const rate = parseFloat(exp.gstRate) || 0;
                                      if (
                                        exp.taxType === "inclusive" &&
                                        rate > 0
                                      ) {
                                        amount = (amount * 100) / (100 + rate);
                                      }
                                      taxable += amount;
                                    },
                                  );

                                  return taxable.toFixed(2);
                                })()}
                              </span>
                            </div>
                          </div>

                          {/* GST Total */}
                          <div className="mb-0">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="">Total GST:</span>
                              <span className=" text-dark">
                                ₹{" "}
                                {(() => {
                                  let totalGST = 0;

                                  // Items GST
                                  (formData.items || []).forEach((item) => {
                                    const qty =
                                      parseFloat(item.sellingQty) || 0;
                                    const price =
                                      parseFloat(item.sellingPrice) || 0;
                                    const rate = parseFloat(item.gstRate) || 0;
                                    const baseAmount = qty * price;

                                    if (
                                      item.taxType === "inclusive" &&
                                      rate > 0
                                    ) {
                                      totalGST +=
                                        (baseAmount * rate) / (100 + rate);
                                    } else if (
                                      item.taxType === "exclusive" &&
                                      rate > 0
                                    ) {
                                      totalGST += (baseAmount * rate) / 100;
                                    }
                                  });

                                  // Services GST
                                  (formData.services || []).forEach(
                                    (service) => {
                                      const unitCost =
                                        parseFloat(service.baseCost) || 0;
                                      const qty = parseFloat(service.qty) || 1;
                                      const rate =
                                        parseFloat(service.gstRate) || 0;
                                      const baseAmount = unitCost * qty;

                                      if (
                                        service.taxType === "inclusive" &&
                                        rate > 0
                                      ) {
                                        totalGST +=
                                          (baseAmount * rate) / (100 + rate);
                                      } else if (
                                        service.taxType === "exclusive" &&
                                        rate > 0
                                      ) {
                                        totalGST += (baseAmount * rate) / 100;
                                      }
                                    },
                                  );

                                  // Expenses GST
                                  (formData.otherExpenses || []).forEach(
                                    (exp) => {
                                      const amount = parseFloat(exp.price) || 0;
                                      const rate = parseFloat(exp.gstRate) || 0;

                                      if (
                                        exp.taxType === "inclusive" &&
                                        rate > 0
                                      ) {
                                        totalGST +=
                                          (amount * rate) / (100 + rate);
                                      } else if (
                                        exp.taxType === "exclusive" &&
                                        rate > 0
                                      ) {
                                        totalGST += (amount * rate) / 100;
                                      }
                                    },
                                  );

                                  return totalGST.toFixed(2);
                                })()}
                              </span>
                            </div>
                          </div>
                          <hr style={{ borderTop: "2px dashed #999" }} />

                          <div className="d-flex justify-content-between rounded">
                            <span className="fw-semibold">GRAND TOTAL:</span>
                            <span className="fw-semibold fs-6">
                              ₹ {(totalAmount || 0).toFixed(2)}
                            </span>
                          </div>
                          <hr style={{ borderTop: "2px dashed #999" }} />

                          {/* Discount Section – place this BEFORE the Final Total div */}
                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="fw-medium">Discount</span>
                              {/* <small className="text-muted">
                                {formData.discountType === "percent"
                                  ? "Percentage"
                                  : "Fixed Amount"}
                              </small> */}

                              <div className="input-group input-group-sm w-50">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control text-end"
                                  placeholder="0.00"
                                  value={formData.discountValue || ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (!/^\d*\.?\d{0,2}$/.test(val)) return;
                                    setFormData((prev) => ({
                                      ...prev,
                                      discountValue: val,
                                    }));
                                  }}
                                />
                                {/* <select
                                className="form-select"
                                style={{ maxWidth: "100px" }}
                                value={formData.discountType || "amount"}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    discountType: e.target.value,
                                    // Optional: reset value when changing type
                                    // discountValue: ""
                                  }))
                                }
                              >
                                <option value="amount">₹ Fixed</option>
                                <option value="percent">% Percent</option>
                              </select> */}
                              </div>
                            </div>
                            {discountAmount > 0 && (
                              <div className="d-flex justify-content-between mt-1">
                                <small className="text-muted">
                                  Discount applied:
                                </small>
                                <small className="text-danger fw-medium">
                                  – ₹ {discountAmount.toFixed(2)}
                                </small>
                              </div>
                            )}
                          </div>

                          {/* <hr className="my-2" /> */}

                          {/* Grand Total – now showing final discounted value */}
                          <div className="py-2">
                            <hr className="my-2" />
                            <div className="d-flex justify-content-between rounded">
                              <span className="fw-semibold">FINAL AMOUNT:</span>
                              <span className="fw-semibold fs-6">
                                ₹ {finalTotal.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Payment Details */}
                        <div className="p-4 border rounded bg-light">
                          <div className="row">
                            <div className="col-md-6">
                              <label className="form-label fw-medium">
                                Amount Received
                              </label>
                              <div className="input-group">
                                <span className="input-group-text">₹</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control text-end"
                                  value={formData.receivedAmount ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (!/^-?\d*\.?\d*$/.test(value)) return;

                                    setFormData((prev) => ({
                                      ...prev,
                                      receivedAmount: value,
                                    }));
                                  }}
                                  onBlur={(e) => {
                                    const value = e.target.value;

                                    // Prevent storing just "-" or "."
                                    if (
                                      value === "-" ||
                                      value === "." ||
                                      value === "-."
                                    ) {
                                      setFormData((prev) => ({
                                        ...prev,
                                        receivedAmount: "",
                                      }));
                                      return;
                                    }

                                    // Optional: format to 2 decimals
                                    if (value) {
                                      setFormData((prev) => ({
                                        ...prev,
                                        receivedAmount:
                                          Number(value).toFixed(2),
                                      }));
                                    }
                                  }}
                                />
                              </div>
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-medium">
                                Payment Mode
                              </label>
                              <select
                                className="form-select"
                                value={formData.paymentMode}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    paymentMode: e.target.value,
                                  }))
                                }
                              >
                                <option value="cash">Cash</option>
                                <option value="bank">Bank Transfer</option>
                                <option value="upi">UPI</option>
                                <option value="card">Card</option>
                                <option value="credit">Credit</option>
                              </select>
                            </div>
                          </div>

                          {/* <hr className="my-4" /> */}
                          <hr style={{ borderTop: "2px dashed #999" }} />

                          {/* Due Balance */}
                          <div className="">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="fw-samibold fs-6">
                                Due Balance:
                              </span>
                              <span
                                className={`fw-samibold fs-6 ${
                                  dueAmount > 0
                                    ? "text-danger"
                                    : dueAmount < 0
                                      ? "text-success"
                                      : "text-muted"
                                }`}
                              >
                                ₹ {Math.abs(dueAmount).toFixed(2)}
                                {dueAmount > 0
                                  ? " (Receivable)"
                                  : dueAmount < 0
                                    ? " (Payable)"
                                    : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className=" d-flex justify-content-end gap-3 p-4 mobile-bottom-space">
                <button
                  type="button"
                  className="btn btn-custom me-2"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={save}
                >
                  {id ? (referenceType ? "Convert into" : "Update") : "Save"}{" "}
                  Sale
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
      {isAddingParty && (
        <AddPartyModal
          isOpen={!!isAddingParty}
          show={isAddingParty}
          context={isAddingParty}
          onClose={() => setIsAddingParty(false)}
          onPartyAdded={(newParty) => {
            setPartyId(newParty._id);
            handlePartySelect(newParty);
          }}
        />
      )}
      <PrefixModal
        show={showAddModal}
        onClose={() => {
          setNewPrefixValue("");
          setShowAddModal(false);
        }}
        title="Add New Prefix"
        label="Prefix Name"
        placeholder="e.g. SALE, INV, BILL"
        value={newPrefixValue}
        onChange={setNewPrefixValue}
        onSave={handleAddPrefix}
        showTypeDropdown={false}
      />
    </>
  );
};
export default headerLayoutWrapper(CreateSalePage);
